import type { AIProvider, ChatMessage, GenerateRequest, GenerateResult, ProviderInfo, StreamHandler, ToolCall, ToolDefinition, UsageStats } from '../types'
import { AiError } from '../types'
import { readJsonOrThrow, readSseData } from '../streaming'
import { fetchOrThrow } from '../network'
import type { ProviderConfig } from '@/types/settings'
import { TEST_PROMPT } from '../prompts'
import { track } from '@/composables/useEventTracker'

/**
 * Adapter para Anthropic (Messages API).
 *
 * Diferencias clave frente a OpenAI:
 *   - Header `x-api-key` + `anthropic-version`.
 *   - System va aparte (no como mensaje).
 *   - Streaming: eventos `content_block_delta` con `delta.text`.
 *   - Uso llega en `message_delta` y `message_start`.
 *   - Requiere el header `anthropic-dangerous-direct-browser-access: true`
 *     para que el navegador permita llamadas directas (CORS). Sin él,
 *     el navegador bloquea la petición antes de llegar a la API.
 *     El nombre "dangerous" es intencional: expone la API key al
 *     usuario; en esta app es BYOK (el usuario aporta su propia key),
 *     así que es aceptable. Para no exponerla, usar el modo Proxy.
 */
const ANTHROPIC_VERSION = '2023-06-01'

export class AnthropicProvider implements AIProvider {
  readonly info: ProviderInfo

  constructor(private cfg: ProviderConfig) {
    this.info = { provider: 'anthropic', model: cfg.model }
  }

  private get headers(): HeadersInit {
    return {
      'Content-Type': 'application/json',
      'x-api-key': this.cfg.apiKey,
      'anthropic-version': ANTHROPIC_VERSION,
      // Opt-in de CORS para uso directo desde el navegador.
      'anthropic-dangerous-direct-browser-access': 'true',
    }
  }

  private get endpoint(): string {
    return `${this.cfg.baseUrl.replace(/\/$/, '')}/messages`
  }

  /**
   * Convierte nuestros `ChatMessage` al formato de bloques de contenido de
   * Anthropic. A diferencia de OpenAI, Anthropic no tiene rol `tool`: los
   * resultados de herramientas viajan como `tool_result` dentro de un
   * mensaje `user`. Y los `tool_calls` del assistant van como bloques
   * `tool_use` en su mensaje.
   */
  private toAnthropicMessages(msgs: ChatMessage[]): {
    role: 'user' | 'assistant'
    content: unknown
  }[] {
    const out: { role: 'user' | 'assistant'; content: unknown }[] = []
    for (const m of msgs) {
      if (m.role === 'system') continue // system va aparte
      if (m.role === 'tool') {
        // tool_result debe ir dentro de un mensaje user. Si el último mensaje
        // acumulado ya es user con tool_results, lo fusionamos; si no, nuevo.
        const last = out[out.length - 1]
        const block = {
          type: 'tool_result' as const,
          tool_use_id: m.toolCallId ?? '',
          content: m.content,
        }
        if (last && last.role === 'user' && Array.isArray(last.content)) {
          last.content.push(block)
        } else {
          out.push({ role: 'user', content: [block] })
        }
        continue
      }
      if (m.role === 'assistant' && m.toolCalls && m.toolCalls.length > 0) {
        // Assistant con tool_use: bloques de texto (si hay) + tool_use.
        const blocks: unknown[] = []
        if (m.content) blocks.push({ type: 'text', text: m.content })
        for (const tc of m.toolCalls) {
          blocks.push({
            type: 'tool_use',
            id: tc.id,
            name: tc.name,
            input: tc.arguments,
          })
        }
        out.push({ role: 'assistant', content: blocks })
        continue
      }
      // Mensaje simple de texto.
      out.push({ role: m.role as 'user' | 'assistant', content: m.content })
    }
    return out
  }

  /** Mapea nuestras ToolDefinition al formato de Anthropic (input_schema). */
  private toAnthropicTools(tools: ToolDefinition[]): unknown[] {
    return tools.map((t) => ({
      name: t.function.name,
      description: t.function.description,
      input_schema: t.function.parameters,
    }))
  }

  private buildBody(req: GenerateRequest, stream: boolean) {
    // Para chat multi-turno usamos el historial (con soporte de bloques para
    // tools); el system del primer mensaje (si lo hay) se extrae aparte.
    // Sin messages, comportamiento habitual: system aparte + 1 user.
    let system: string | undefined
    let messages: unknown
    if (req.messages && req.messages.length > 0) {
      const sysMsg = req.messages.find((m) => m.role === 'system')
      system = sysMsg?.content ?? req.system
      messages = this.toAnthropicMessages(req.messages)
    } else {
      system = req.system
      messages = [{ role: 'user', content: req.user }]
    }

    const body: Record<string, unknown> = {
      model: this.cfg.model,
      max_tokens: req.maxTokens ?? 1024,
      temperature: req.temperature ?? 0.4,
      system,
      messages,
      stream,
    }

    // Function-calling: Anthropic usa `tools` con `input_schema` (no
    // `parameters`) y `tool_choice` con formato propio.
    if (req.tools && req.tools.length > 0) {
      body.tools = this.toAnthropicTools(req.tools)
      const choice = req.toolChoice ?? 'auto'
      if (choice === 'auto') body.tool_choice = { type: 'auto' }
      else if (choice === 'none') body.tool_choice = { type: 'none' }
      else if (typeof choice === 'object') {
        body.tool_choice = { type: 'tool', name: choice.function.name }
      }
    } else if (req.toolChoice === 'none') {
      body.tool_choice = { type: 'none' }
    }
    return body
  }

  async stream(
    req: GenerateRequest,
    onToken: StreamHandler,
  ): Promise<GenerateResult> {
    this.requireApiKey()

    const response = await fetchOrThrow(this.endpoint, {
      method: 'POST',
      headers: this.headers,
      body: JSON.stringify(this.buildBody(req, true)),
      signal: req.signal,
    }, 'Anthropic')

    if (!response.ok) {
      await readJsonOrThrow(response)
      throw new AiError('Respuesta inválida de Anthropic.', response.status)
    }

    let text = ''
    const usage: UsageStats = { tokensIn: null, tokensOut: null }
    // Acumulador de tool_use: por `index` del content_block. Cada bloque
    // tool_use se abre en `content_block_start` (id+name) y sus argumentos
    // JSON llegan fragmentados vía `input_json_delta`.
    const toolAccum = new Map<number, { id: string; name: string; args: string }>()

    for await (const data of readSseData(response, req.signal)) {
      let event: {
        type: string
        index?: number
        content_block?: { type?: string; id?: string; name?: string; text?: string }
        delta?: { type?: string; text?: string; partial_json?: string }
        message?: { usage?: { input_tokens?: number } }
        usage?: { output_tokens?: number }
      }
      try {
        event = JSON.parse(data)
      } catch {
        continue
      }

      // Apertura de un content_block: si es tool_use, registramos id+name.
      if (
        event.type === 'content_block_start' &&
        event.content_block?.type === 'tool_use' &&
        event.index != null
      ) {
        toolAccum.set(event.index, {
          id: event.content_block.id ?? '',
          name: event.content_block.name ?? '',
          args: '',
        })
      }
      // Deltas: texto normal o fragmento JSON de tool_use (input_json_delta).
      if (event.type === 'content_block_delta') {
        if (event.delta?.text) {
          text += event.delta.text
          onToken(event.delta.text)
        }
        if (event.delta?.type === 'input_json_delta' && event.delta.partial_json && event.index != null) {
          const acc = toolAccum.get(event.index)
          if (acc) acc.args += event.delta.partial_json
        }
      }
      // Input tokens al iniciar el mensaje.
      if (event.type === 'message_start' && event.message?.usage?.input_tokens != null) {
        usage.tokensIn = event.message.usage.input_tokens
      }
      // Output tokens al terminar.
      if (event.type === 'message_delta' && event.usage?.output_tokens != null) {
        usage.tokensOut = event.usage.output_tokens
      }
    }

    // Reconstruir ToolCall[] parseando el JSON de cada tool_use.
    const toolCalls: ToolCall[] = []
    for (const [, acc] of toolAccum) {
      if (!acc.name && !acc.args) continue
      let args: Record<string, unknown> = {}
      if (acc.args) {
        try {
          args = JSON.parse(acc.args)
        } catch {
          args = {}
        }
      }
      toolCalls.push({ id: acc.id, name: acc.name, arguments: args })
    }

    // Métrica global: tokens IA consumidos (sin contenido sensible).
    void track('ai_request', {
      provider: 'anthropic',
      model: this.cfg.model,
      tokens: (usage.tokensIn ?? 0) + (usage.tokensOut ?? 0),
      stream: true,
    })
    return { text, usage, toolCalls: toolCalls.length > 0 ? toolCalls : undefined }
  }

  async generate(req: GenerateRequest): Promise<GenerateResult> {
    this.requireApiKey()
    const response = await fetchOrThrow(this.endpoint, {
      method: 'POST',
      headers: this.headers,
      body: JSON.stringify(this.buildBody(req, false)),
      signal: req.signal,
    }, 'Anthropic')
    const data = await readJsonOrThrow<{
      content: {
        type: string
        text?: string
        id?: string
        name?: string
        input?: Record<string, unknown>
      }[]
      usage: { input_tokens?: number; output_tokens?: number }
    }>(response)
    // El texto es la unión de los bloques 'text'; los tool_use van aparte.
    const text = data.content
      .filter((b) => b.type === 'text' && b.text)
      .map((b) => b.text)
      .join('')
    const toolCalls: ToolCall[] = data.content
      .filter((b) => b.type === 'tool_use')
      .map((b) => ({
        id: b.id ?? '',
        name: b.name ?? '',
        arguments: b.input ?? {},
      }))
    const usage = {
      tokensIn: data.usage.input_tokens ?? null,
      tokensOut: data.usage.output_tokens ?? null,
    }
    // Métrica global: tokens IA consumidos (sin contenido sensible).
    void track('ai_request', {
      provider: 'anthropic',
      model: this.cfg.model,
      tokens: (usage.tokensIn ?? 0) + (usage.tokensOut ?? 0),
      stream: false,
    })
    return {
      text,
      usage,
      toolCalls: toolCalls.length > 0 ? toolCalls : undefined,
    }
  }

  async testConnection(): Promise<void> {
    this.requireApiKey()
    const result = await this.generate({
      user: TEST_PROMPT,
      maxTokens: 5,
      temperature: 0,
    })
    if (!result.text) throw new AiError('Anthropic devolvió una respuesta vacía.')
  }

  private requireApiKey() {
    if (!this.cfg.apiKey) {
      throw new AiError('Falta la API key de Anthropic. Configúrala en Ajustes.')
    }
  }
}
